import { createBrowserHistory, createMemoryHistory } from 'history';

import { removeBasename, createURL } from '../path';

import { HistoryInterface } from './types';

const PREVHISTORY_STACK_LIMIT = 10;

/**
 * Создает историю с поддержкой стека предыдущих состояний
 *
 * Расширяет стандартную history (browser/memory) следующими возможностями:
 * - Сохраняет стек предыдущих состояний в state.prev
 * - Поддерживает обработку basenames для виджета sdk
 * - Добавляет методы createURL и encodeLocation для поддержки HistoryRouter
 *
 * @param historyCreator - Функция создания истории (createBrowserHistory или createMemoryHistory)
 * @param role - Роль текущего модуля (используется для фильтрации путей в много-модульных приложениях)
 * @param basenames - Массив базовых путей модулей
 * @param basename - Базовый путь текущего модуля
 * @returns Функция, создающая расширенную историю
 *
 * @example
 * const history = createHistoryWidthPrev(createBrowserHistory)()
 */
export const createHistoryWidthPrev =
  (
    historyCreator: typeof createBrowserHistory | typeof createMemoryHistory,
    role?: string,
    basenames?: string[],
    basename?: string,
  ) =>
  (...args: Parameters<typeof historyCreator>) => {
    const historyInitiated = historyCreator(...args) as HistoryInterface;
    const prevPush = historyInitiated.push;

    const prevStateStack: Array<{ pathname: string; search: string }> = [];

    const pushToPrevHistoryStack = (prevState: {
      pathname: string;
      search: string;
    }) => {
      if (prevStateStack.length === PREVHISTORY_STACK_LIMIT) {
        prevStateStack.pop();
      }
      prevStateStack.unshift(prevState);
    };

    const locationWithoutRole = (pathname: string) => {
      if (
        basenames &&
        basenames.length > 1 &&
        role &&
        pathname.includes(`/${role}`) &&
        basenames.some(
          (moduleBasename, index) =>
            !!index && pathname.startsWith(moduleBasename),
        )
      ) {
        return pathname.split(`/${role}`)[1];
      }
      return pathname;
    };

    historyInitiated.push = (location, state) => {
      let localBasename = '';

      if (
        basenames &&
        basenames.length > 1 &&
        historyInitiated.location.pathname === '/'
      ) {
        pushToPrevHistoryStack({
          pathname: basenames[0],
          search: historyInitiated.location.search,
        });
      } else {
        pushToPrevHistoryStack({
          pathname: historyInitiated.location.pathname,
          search: historyInitiated.location.search,
        });
      }
      if (typeof location === 'string') {
        if (basename) {
          localBasename = location.includes(basename)
            ? ''
            : basename.replace(/\/+$/, '');
        }

        location = locationWithoutRole(location);

        prevPush(`${localBasename}${removeBasename(location, basenames)}`, {
          prev: prevStateStack,
        });
      } else {
        if (location?.pathname) {
          if (basename) {
            localBasename = location.pathname.includes(basename)
              ? ''
              : basename.replace(/\/+$/, '');
          }

          location.pathname = locationWithoutRole(location.pathname);
        }

        prevPush(
          {
            ...location,
            ...(location.pathname
              ? {
                  pathname: `${localBasename}${removeBasename(
                    location.pathname,
                    basenames,
                  )}`,
                }
              : {}),
          },
          {
            ...Object.assign({}, state),
            prev: prevStateStack,
          },
        );
      }
    };

    historyInitiated.createURL = createURL;

    historyInitiated.encodeLocation = (to) => {
      let url = createURL(to);
      return {
        pathname: url.pathname,
        search: url.search,
        hash: url.hash,
      };
    };

    return historyInitiated;
  };

export type History = HistoryInterface;
export const history = createHistoryWidthPrev(createBrowserHistory)();
/* eslint-disable-next-line @typescript-eslint/no-redeclare */
export const History = Symbol('History');
